import { useCallback, useEffect, useRef } from 'react'
import { useActiveLessonStore } from '@/stores/activeLessonStore'
import { subscribeToReceivedNotifications } from '@/stores/notificationsStore'
import { useEchoClient } from './echoClient'
import { listenSharedPrivateChannel } from './sharedPrivateChannel'
import type { LessonScheduleChangedPayload, LessonStatusChangedPayload } from './useLessonRealtime'

/**
 * Уведомления, после которых у пользователя может появиться текущий урок:
 * урок начался или вот-вот начнётся. Сервер шлёт их и учителю, и участникам.
 */
const LESSON_APPEAR_EVENT_KEYS: ReadonlySet<string> = new Set([
  'lesson.started',
  'lesson.start_soon',
])

/**
 * Пауза перед перезапросом. Уведомление о старте, событие статуса и возврат на
 * вкладку часто приходят почти разом, а правка расписания присылает по событию
 * на каждый удалённый и созданный урок — в API уходит один запрос на пачку.
 */
const REFRESH_DEBOUNCE_MS = 500

/**
 * Запас после meta.next_refresh_at: момент считается по часам сервера, и
 * запрос ровно в него может застать ещё прежний ответ.
 */
const NEXT_REFRESH_MARGIN_MS = 1_500

/**
 * Потолок таймера. setTimeout с задержкой больше 2^31−1 мс (~24,8 суток)
 * срабатывает сразу. API не отдаёт момент дальше 24 часов, а потолок страхует
 * от сбитых часов браузера: таймер перевзведётся следующим ответом.
 */
const NEXT_REFRESH_MAX_DELAY_MS = 24 * 60 * 60 * 1_000

/** Первая пауза перед повтором сорвавшегося запроса; дальше она удваивается. */
const FAILED_FETCH_RETRY_BASE_MS = 1_500

/**
 * Потолок паузы между повторами: в быстрой серии после временного сбоя запроса
 * и если сервер снова отдаёт уже наступивший момент. Повторы по таймеру идут
 * только в этих двух случаях.
 */
const RETRY_MAX_DELAY_MS = 60_000

/**
 * Длина быстрой серии: сколько раз подряд повторяется запрос после временного
 * сбоя — паузы 1,5 → 3 → 6 … до {@link RETRY_MAX_DELAY_MS}, вместе около 6 минут.
 * Сбой дольше — затяжной (API лежит или эндпоинт стабильно отвечает 5xx), и
 * повторов по таймеру больше нет: иначе каждая открытая вкладка опрашивала бы API,
 * пока он лежит, — тот же поллинг. Дальше плашку обновят события урока, возврат
 * во вкладку, `online` и таймер next_refresh_at.
 */
const FAILED_FETCH_RETRY_ATTEMPTS = 10

/**
 * Отложенный перезапрос GET /lessons/active: частые триггеры схлопываются в
 * один запрос. Ожидающий запрос снимается при выходе и смене пользователя.
 */
function useDebouncedRefresh(userId: number | null): () => void {
  const fetchActiveLesson = useActiveLessonStore((state) => state.fetch)
  const timerRef = useRef<number | null>(null)

  const cancel = useCallback(() => {
    if (timerRef.current === null) return
    window.clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  useEffect(() => {
    return cancel
  }, [cancel, userId])

  return useCallback(() => {
    cancel()
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      void fetchActiveLesson()
    }, REFRESH_DEBOUNCE_MS)
  }, [cancel, fetchActiveLesson])
}

/**
 * Появление плашки: уведомления о старте урока, пришедшие в реальном времени.
 * История из bootstrap сюда не попадает (см. subscribeToReceivedNotifications),
 * поэтому старые записи не вызывают запросов, а запросы — новых уведомлений.
 */
function useLessonNotificationsRefresh(userId: number | null, requestRefresh: () => void): void {
  useEffect(() => {
    if (!userId) return
    return subscribeToReceivedNotifications((notification) => {
      if (LESSON_APPEAR_EVENT_KEYS.has(notification.event_key)) requestRefresh()
    })
  }, [requestRefresh, userId])
}

/**
 * Исчезновение и смена статуса: одна подписка на приватный канал статуса
 * урока, который сейчас на плашке. Presence-канал `lesson.{id}` не годится:
 * его авторизация ставит ученику отметку присутствия, и плашка проставляла бы
 * «был на занятии» любому, кто просто открыл кабинет.
 *
 * После каждой успешной подписки — перезапрос: событие статуса, ушедшее между
 * ответом API и подпиской или за время обрыва сокета, сервер не повторит.
 * Повторный ответ с тем же уроком подписку не пересоздаёт, цикла нет.
 */
function useActiveLessonStatusChannel(userId: number | null, requestRefresh: () => void): void {
  const lessonId = useActiveLessonStore((state) => state.lesson?.id ?? null)
  const applyStatusChange = useActiveLessonStore((state) => state.applyStatusChange)
  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  useEffect(() => {
    if (!userId || lessonId === null) return
    // Смена урока снимает подписку на прежний канал, уход плашки — на последний
    return listenSharedPrivateChannel<LessonStatusChangedPayload>(
      echo,
      `lesson.${lessonId}.status`,
      '.lesson.status.changed',
      (payload) => {
        // Сначала полезная нагрузка: завершённый или отменённый урок гаснет
        // сразу, начатый сразу «идёт». Затем ответ API — может появиться другой урок.
        applyStatusChange(payload)
        requestRefresh()
      },
      requestRefresh,
    )
  }, [applyStatusChange, echo, lessonId, requestRefresh, userId])
}

/**
 * Урок появился, исчез или сдвинулся по времени: событие `lesson.schedule.changed`
 * личного канала `user.{id}.lessons` — урок создан или перенесён, сменился состав
 * участников, правка расписания или отпуск удалили и создали авто-уроки. Канал
 * статуса урока на плашке получает это событие тоже, но только про показанный
 * урок, а плашке нужен и урок, созданный или перенесённый в окно показа, пока её
 * нет. Поэтому слушаем только личный канал — без двойной обработки одного события.
 *
 * Нагрузка в стор не применяется, только перезапрос: видимость решает API. Событие
 * про удалённый урок или исключённого участника несёт прежнее время урока и по
 * нагрузке не отличается от неизменённого, а новое время урока, перенесённого из
 * окна показа, на миг показало бы плашку, которой в ответе API уже нет. Серия
 * событий одной правки расписания схлопывается общей паузой в один запрос.
 *
 * Перезапроса после подписки нет: onSubscribed зовётся и на первой подписке, и на
 * повторной после обрыва сокета, различить их нельзя, а на первой запрос повторил бы
 * ответ, полученный при входе в кабинет. Событие, ушедшее за время обрыва, теряется:
 * при показанной плашке ответ сверит переподписка канала статуса, без неё изменение
 * дойдёт по возврату во вкладку или напоминанию «Скоро начнётся урок».
 * Устойчивость к переподключению — отдельная задача EA-591.
 */
function useLessonScheduleChannel(userId: number | null, requestRefresh: () => void): void {
  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  useEffect(() => {
    if (!userId) return
    // Выход и смена пользователя снимают подписку на личный канал прежнего
    return listenSharedPrivateChannel<LessonScheduleChangedPayload>(
      echo,
      `user.${userId}.lessons`,
      '.lesson.schedule.changed',
      () => requestRefresh(),
    )
  }, [echo, requestRefresh, userId])
}

/**
 * Подстраховка на возврат во вкладку и в сеть: вкладка могла часами висеть в
 * фоне, где браузер душит таймеры, а после сна ноутбука она становится видимой
 * раньше, чем поднимается сеть, — тогда перезапрос по `online`.
 */
function useVisibilityRefresh(userId: number | null, requestRefresh: () => void): void {
  useEffect(() => {
    if (!userId) return
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestRefresh()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('online', requestRefresh)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('online', requestRefresh)
    }
  }, [requestRefresh, userId])
}

/**
 * Сорвался ли подряд временным сбоем уже больше запросов, чем повторяет быстрая
 * серия: повторов по таймеру больше нет.
 */
function isRetrySeriesOver(failedAttempts: number): boolean {
  return failedAttempts > FAILED_FETCH_RETRY_ATTEMPTS
}

/**
 * Быстрая серия повторов после временного сбоя: паузы 1,5 → 3 → 6 … до 60 с, не
 * больше {@link FAILED_FETCH_RETRY_ATTEMPTS} раз подряд. Без неё сбой первого
 * запроса после входа (502 при деплое API, обрыв сети) оставлял кабинет без
 * плашки до смены вкладки: таймеру нечего ждать, канала статуса нет, а
 * уведомление о старте урока пришло раньше. То же после фонового запроса по
 * событию: ответ, ради которого он шёл, иначе потерялся бы до next_refresh_at.
 *
 * Серия кончилась, а API всё отвечает сбоем, — повторов по таймеру больше нет, это
 * был бы поллинг: каждая открытая вкладка слала бы запросы, пока API лежит. Дальше
 * запрос уходит только по событию урока, возврату во вкладку, `online` и таймеру
 * next_refresh_at (useNextRefreshTimer), каждый — один раз; первый успешный ответ
 * обнуляет счётчик. Цена: вкладка, пролежавшая весь сбой открытой и видимой, узнает
 * об уроке, начатом за это время, при следующем из этих событий.
 *
 * Повторов нет и в скрытой вкладке: плашку там никто не видит, а возврат во вкладку
 * сам отправит запрос (useVisibilityRefresh), и его сбой продолжит серию. Без сети
 * тоже: запрос отправит событие `online`.
 *
 * Постоянный сбой (4xx) стор не повторяет. После временных он обнуляет счётчик —
 * повторы прекращаются, а useNextRefreshTimer снова взводит таймер. При нулевом
 * счётчике стор не меняется: если сорвался запрос самого таймера, таймер не
 * перевзводится, и плашку обновят возврат во вкладку, сеть и события урока.
 * Повторы прекращаются и первым успешным ответом, выходом и сменой пользователя.
 */
function useFailedFetchRetry(userId: number | null): void {
  const fetchActiveLesson = useActiveLessonStore((state) => state.fetch)
  const failedAttempts = useActiveLessonStore((state) => state.failedAttempts)

  useEffect(() => {
    if (!userId || failedAttempts === 0 || isRetrySeriesOver(failedAttempts)) return
    if (!navigator.onLine || document.visibilityState === 'hidden') return
    const delayMs = Math.min(FAILED_FETCH_RETRY_BASE_MS * 2 ** (failedAttempts - 1), RETRY_MAX_DELAY_MS)
    const timerId = window.setTimeout(() => {
      // Вкладку спрятали, пока шла пауза: серия встаёт до возврата во вкладку
      if (document.visibilityState === 'hidden') return
      void fetchActiveLesson()
    }, delayMs)
    return () => window.clearTimeout(timerId)
  }, [failedAttempts, fetchActiveLesson, userId])
}

/**
 * Один таймер на meta.next_refresh_at — момент, когда ответ может смениться без
 * событий (урок вошёл в окно показа, запланированный закончился, идущий
 * перестал считаться текущим). Перевзводится по каждому ответу.
 */
function useNextRefreshTimer(userId: number | null): void {
  const fetchActiveLesson = useActiveLessonStore((state) => state.fetch)
  const nextRefreshAt = useActiveLessonStore((state) => state.nextRefreshAt)
  // Номер ответа в зависимостях: таймер перевзводится, даже если момент в новом
  // ответе тот же, что в прежнем
  const responseVersion = useActiveLessonStore((state) => state.responseVersion)
  const failedAttempts = useActiveLessonStore((state) => state.failedAttempts)
  const overdueRef = useRef<{ atMs: number; attempts: number } | null>(null)

  useEffect(() => {
    // Пока идёт быстрая серия повторов после временного сбоя (useFailedFetchRetry),
    // этот таймер молчит: он слал бы запросы наперегонки с ней. Перевзводят его
    // конец серии, первый успешный ответ и постоянный сбой после временных (оба
    // обнуляют счётчик). Постоянный сбой при нулевом счётчике стор не меняет:
    // ждущий таймер остаётся, а сработавший не перевзводится.
    if (!userId || !nextRefreshAt) return
    if (failedAttempts > 0 && !isRetrySeriesOver(failedAttempts)) return
    const atMs = Date.parse(nextRefreshAt)
    if (!Number.isFinite(atMs)) return

    const leftMs = atMs - Date.now()
    let delayMs: number
    if (leftMs > 0) {
      overdueRef.current = null
      delayMs = leftMs + NEXT_REFRESH_MARGIN_MS
    } else if (failedAttempts > 0) {
      // Серия кончилась, а сохранённый момент уже прошёл: запрос после него уже был
      // и сорвался — повтором серии, запросом самого таймера или по событию. Новый
      // запрос по тому же моменту был бы повтором по таймеру, то есть поллингом, —
      // ждём события урока, возврата во вкладку или `online`
      return
    } else {
      // Момент по часам браузера уже наступил, а сервер отдал его снова — часы
      // браузера спешат. Пауза растёт, чтобы повторы не превратились в поллинг.
      const overdue = overdueRef.current
      const attempts = overdue && overdue.atMs === atMs ? overdue.attempts + 1 : 0
      overdueRef.current = { atMs, attempts }
      delayMs = Math.min(NEXT_REFRESH_MARGIN_MS * 2 ** attempts, RETRY_MAX_DELAY_MS)
    }

    const timerId = window.setTimeout(() => {
      void fetchActiveLesson()
    }, Math.min(delayMs, NEXT_REFRESH_MAX_DELAY_MS))
    return () => window.clearTimeout(timerId)
  }, [failedAttempts, fetchActiveLesson, nextRefreshAt, responseVersion, userId])
}

/**
 * Живое обновление глобальной плашки текущего урока без перезагрузки и без
 * поллинга по интервалу. Перезапрос GET /lessons/active запускают:
 *
 * - уведомления о старте урока `lesson.started` и `lesson.start_soon`;
 * - событие `lesson.status.changed` канала статуса урока на плашке (нагрузка
 *   сразу применяется к стору) и каждая успешная подписка на этот канал;
 * - событие `lesson.schedule.changed` личного канала `user.{id}.lessons`: урок
 *   создан или перенесён, сменился состав участников, правка расписания или
 *   отпуск удалили и создали авто-уроки;
 * - возврат во вкладку и в сеть.
 *
 * Эти триггеры идут через одну паузу {@link REFRESH_DEBOUNCE_MS}. Помимо них — один
 * таймер на meta.next_refresh_at и быстрая серия повторов после временного сбоя
 * запроса: не больше {@link FAILED_FETCH_RETRY_ATTEMPTS} раз подряд и только в
 * видимой вкладке; после неё повторов по таймеру нет.
 *
 * Подписки — только у авторизованного пользователя; выход, смена пользователя и
 * пересоздание Echo их снимают и ставят заново.
 *
 * Сокет недоступен — ошибок пользователю нет: плашка живёт на последнем ответе
 * и обновляется по таймеру и возврату во вкладку.
 */
export function useActiveLessonRealtime(userId: number | null): void {
  const requestRefresh = useDebouncedRefresh(userId)

  useLessonNotificationsRefresh(userId, requestRefresh)
  useActiveLessonStatusChannel(userId, requestRefresh)
  useLessonScheduleChannel(userId, requestRefresh)
  useVisibilityRefresh(userId, requestRefresh)
  useFailedFetchRetry(userId)
  useNextRefreshTimer(userId)
}
